// Guide & Tips — static reference cards. Ported verbatim from the legacy
// views.guide; the only change is the `(app)` mount-element signature.
export function guideView(app) {
  app.innerHTML = `
    <section class="space-y-6">
      <div>
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Guide & Tips</h2>
        <p class="text-sage-600 text-sm">Memory tricks and patterns to help you internalize Portuguese verbs faster.</p>
      </div>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📐 Regular verbs — the foundation</h3>
        <p class="text-sm text-sage-700 mb-3">Portuguese has three regular conjugation patterns based on the infinitive ending:</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-AR verbs</p>
            <p class="text-xs text-sage-500 mb-2">falar (to speak)</p>
            <p class="font-mono text-xs leading-relaxed">o, as, a,<br>amos, ais, am</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-ER verbs</p>
            <p class="text-xs text-sage-500 mb-2">comer (to eat)</p>
            <p class="font-mono text-xs leading-relaxed">o, es, e,<br>emos, eis, em</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-IR verbs</p>
            <p class="text-xs text-sage-500 mb-2">partir (to leave)</p>
            <p class="font-mono text-xs leading-relaxed">o, es, e,<br>imos, is, em</p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3">💡 Tip: -ER and -IR verbs are identical except for nós and vós.</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🔄 Stem-changing verbs — the boot</h3>
        <p class="text-sm text-sage-700 mb-3">Some change only in the <b>eu</b> form; the <b>-ear</b> and <b>-iar</b> verbs change across the whole boot — every form EXCEPT nós and vós (the heel of the boot). Draw a line around the changed forms and it looks like a boot.</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3 max-w-xs">
          <div class="grid grid-cols-2 gap-1">
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">passeio</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">passeias</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">passeia</div>
            <div class="px-2 py-1">passeamos</div>
            <div class="px-2 py-1">passeais</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">passeiam</div>
          </div>
        </div>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>-ear → ei</b> (whole boot): passear, pentear, recear</li>
          <li><b>-iar → ei</b> (odiar group): odiar, ansiar, mediar</li>
          <li><b>e → i</b> in the eu form (only -ir): servir, sentir, repetir</li>
          <li><b>o → u</b> in the eu form: dormir, cobrir, tossir</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">👤 Eu-only irregulars — the first-person oddballs</h3>
        <p class="text-sm text-sage-700 mb-3">A group of verbs is completely regular except in the "eu" form, where the ending shifts to <b>-ço</b>, <b>-go</b> or <b>-nho</b>:</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3">
          ter → ten<span class="text-red-600 font-bold">ho</span> · pôr → pon<span class="text-red-600 font-bold">ho</span> · fazer → fa<span class="text-red-600 font-bold">ço</span> · pedir → pe<span class="text-red-600 font-bold">ço</span> · ouvir → ou<span class="text-red-600 font-bold">ço</span> · dizer → di<span class="text-red-600 font-bold">go</span> · trazer → tra<span class="text-red-600 font-bold">go</span>
        </div>
        <p class="text-xs text-sage-500">💡 Memorize the list as a chant: "tenho, ponho, faço, peço, ouço, digo, trago."</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">⚡ Fully irregular — the big four</h3>
        <p class="text-sm text-sage-700 mb-3">Four verbs are so common and so irregular they deserve dedicated memorization: <b>ser</b>, <b>estar</b>, <b>ir</b>, and <b>ter</b>. Strange fact: in the preterite tense, <i>ser</i> and <i>ir</i> are identical (fui, foste, foi…) — context decides which it is.</p>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>ser vs estar</b>: ser is for permanent traits (I am tall), estar for temporary states & location (I am tired, I am at home).</li>
          <li><b>saber vs conhecer</b>: saber for facts/skills, conhecer for people and places.</li>
          <li><b>ir + infinitive</b> = "going to" do something. Vou comer = I'm going to eat.</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📅 The three core tenses</h3>
        <div class="space-y-3 text-sm text-sage-700">
          <div>
            <p class="font-bold text-sage-800">Presente — happening now or habitually</p>
            <p>"Eu falo português" = I speak Portuguese / I am speaking Portuguese.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Pretérito Perfeito — completed actions in the past</p>
            <p>"Falei com a Maria ontem" = I spoke with Maria yesterday. Use this for one-time, finished events.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Futuro Simples — what will happen</p>
            <p>"Falarei com a Maria amanhã" = I will speak with Maria tomorrow. <b>Quick win:</b> for all regular verbs, just add ei, ás, á, emos, eis, ão to the full infinitive — no chopping off endings.</p>
          </div>
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🪞 Reflexive verbs</h3>
        <p class="text-sm text-sage-700 mb-2">Verbs ending in <b>-se</b> are reflexive — the action is done to oneself. Conjugate normally, then attach the matching pronoun (me, te, se, nos, vos, se). In European Portuguese it usually follows the verb (enclisis), but jumps in front after <i>não</i>, <i>que</i> or an adverb: <i>não me levanto</i>.</p>
        <div class="bg-sage-50 rounded-lg p-3 font-mono text-sm">
          levantar-se → levanto-me, levantas-te, levanta-se…
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🎯 Study strategy</h3>
        <ol class="text-sm text-sage-700 space-y-2 list-decimal pl-5">
          <li><b>Start with the top 50.</b> They cover ~70% of everyday speech.</li>
          <li><b>Learn one tense at a time.</b> Master Presente before moving on.</li>
          <li><b>Drill the eu-form irregulars as a set.</b> They share a single shortcut.</li>
          <li><b>Use the trainer daily.</b> Even 5 minutes builds strong recall.</li>
          <li><b>Track mistakes.</b> Verbs you miss often deserve flashcard time.</li>
        </ol>
      </article>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
